import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import Header from "../components/Header";
import RelatedSearches from "../components/RelatedSearches";
import FiltersPanel from "../components/FiltersPanel";
import MasonryGrid from "../components/MasonryGrid";
import PhotoDrawer from "../components/PhotoDrawer";
import Footer from "../components/Footer";
import { searchPhotos, recordSearchEvent } from "../lib/api";

const PER_PAGE = 24;
const SUGGESTED = ["nature", "architecture", "portrait", "abstract", "travel"];

const ORIENTATION_LABELS = { landscape: "Horizontal", portrait: "Vertical", square: "Square" };

function formatNumber(n) {
  if (n == null) return "";
  try { return Number(n).toLocaleString(); } catch { return String(n); }
}

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = (searchParams.get("q") || "").trim();
  const orientation = searchParams.get("orientation") || "";
  const color = searchParams.get("color") || "";
  const openId = searchParams.get("photo");
  const nav = useNavigate();

  useEffect(() => {
    if (openId) nav(`/dashboard/photo/${openId}`, { replace: true });
  }, [openId, nav]);

  const [photos, setPhotos] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [total, setTotal] = useState(null);
  const [initialPhoto, setInitialPhoto] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => { if (q) recordSearchEvent(q); }, [q]);

  const load = useCallback(async (pg) => {
    if (!q) return;
    setLoading(true);
    setError("");
    try {
      const data = await searchPhotos({
        query: q, page: pg, per_page: PER_PAGE,
        orientation: orientation || undefined, color: color || undefined,
      });
      const arr = data.photos || [];
      setPhotos((prev) => {
        if (pg === 1) return arr;
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...arr.filter((p) => !seen.has(p.id))];
      });
      setTotal(data.total_results ?? null);
      setHasMore(Boolean(data.next_page) && arr.length > 0);
    } catch (e) {
      setError(e?.response?.data?.detail?.error || e?.message || "Search failed");
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [q, orientation, color]);

  useEffect(() => {
    setPhotos([]); setPage(1); setHasMore(true); load(1);
  }, [q, orientation, color, load]);

  const loadMore = () => {
    if (loading || !hasMore) return;
    const next = page + 1;
    setPage(next);
    load(next);
  };

  const updateParams = (patch) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === undefined || v === null || v === "") params.delete(k);
      else params.set(k, v);
    });
    params.delete("photo");
    setSearchParams(params);
  };

  const clearAllFilters = () => updateParams({ orientation: "", color: "" });

  const appliedCount = useMemo(() => (orientation ? 1 : 0) + (color ? 1 : 0), [orientation, color]);

  const openPhoto = (p) => nav(`/dashboard/photo/${p.id}`);
  const closeDrawer = () => {};

  const pickRelated = (term) => {
    const params = new URLSearchParams(searchParams);
    params.set("q", term);
    params.delete("photo");
    setSearchParams(params);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header compact defaultQuery={q} />

      <div className="pt-16">
        <RelatedSearches query={q} onPick={pickRelated} />

        <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-6 pb-4">
          <div className="text-[11px] uppercase tracking-[0.35em] text-muted-foreground">
            Search results
          </div>
          <h1
            className="font-serif text-3xl md:text-5xl text-ink mt-2 capitalize"
            data-testid="search-title"
          >
            {q ? `${q} royalty-free images` : "Explore the collection"}
          </h1>
          {total != null && (
            <div className="mt-2 text-sm text-muted-foreground" data-testid="search-total-line">
              {formatNumber(total)} {q} stock photos and images
            </div>
          )}
        </div>

        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-3 flex flex-wrap items-center gap-3 border-y border-border bg-ivory/70 backdrop-blur">
          <button
            onClick={() => setFiltersOpen(true)}
            data-testid="filters-open-btn"
            className="inline-flex items-center gap-2 px-4 py-2 border border-ink bg-white hover:bg-gold hover:text-ivory hover:border-gold transition-colors text-sm"
          >
            <SlidersHorizontal className="h-4 w-4" /> Filters
            {appliedCount > 0 && (
              <span className="ml-1 h-5 min-w-5 px-1.5 rounded-full bg-gold text-ivory text-[11px] inline-flex items-center justify-center">
                {appliedCount}
              </span>
            )}
          </button>

          {orientation && (
            <button
              onClick={() => updateParams({ orientation: "" })}
              data-testid="applied-filter-orientation"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-border text-sm text-ink hover:border-ink"
            >
              {ORIENTATION_LABELS[orientation] || orientation}
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          {color && (
            <button
              onClick={() => updateParams({ color: "" })}
              data-testid="applied-filter-color"
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-border text-sm text-ink hover:border-ink capitalize"
            >
              <span className="h-3.5 w-3.5 rounded-full border border-border" style={{ background: color }} />
              {color}
              <X className="h-3.5 w-3.5" />
            </button>
          )}

          {appliedCount > 0 && (
            <button
              onClick={clearAllFilters}
              className="text-xs text-muted-foreground hover:text-ink underline underline-offset-4"
              data-testid="clear-all-filters"
            >
              Clear all
            </button>
          )}

          <div className="ml-auto text-xs text-muted-foreground hidden md:block">
            Sorted by relevance
          </div>
        </div>

        <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-8 pb-24">
          {error && (
            <div
              data-testid="search-error"
              className="mb-6 p-4 border border-destructive/30 bg-destructive/5 text-destructive text-sm"
            >
              {error}
            </div>
          )}

          <MasonryGrid
            photos={photos}
            onOpen={openPhoto}
            loading={loading}
            hasMore={hasMore}
            onLoadMore={loadMore}
            testId="search-masonry"
            emptyState={
              !loading && !error && photos.length === 0 ? (
                <div data-testid="empty-state" className="py-24 text-center max-w-lg mx-auto">
                  <div className="font-serif text-3xl text-ink">No results for &ldquo;{q}&rdquo;.</div>
                  <p className="text-muted-foreground mt-3 text-sm">
                    Try adjusting filters or explore a curated category:
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center mt-5">
                    {SUGGESTED.map((s) => (
                      <Link
                        key={s}
                        to={`/dashboard/search?q=${s}`}
                        className="px-3 py-1.5 text-sm border border-border hover:border-ink hover:bg-white"
                      >
                        {s}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : null
            }
          />
        </section>
      </div>

      <FiltersPanel
        open={filtersOpen}
        orientation={orientation}
        color={color}
        onChange={updateParams}
        onClose={() => setFiltersOpen(false)}
        onClearAll={clearAllFilters}
      />

      <Footer />

      <PhotoDrawer
        open={Boolean(openId)}
        photoId={openId}
        initialPhoto={initialPhoto && String(initialPhoto.id) === String(openId) ? initialPhoto : null}
        onClose={closeDrawer}
        onOpenPhoto={openPhoto}
      />
    </div>
  );
}
